import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import f from 'odata-filter-builder';
import buildQuery from 'odata-query';
import { environment } from '../../../environments/environment';
import { HttpService } from '../http.service';
import { JobTitleGroupModel } from '../models/hiring/jobTitleGroup.model';
import { ServiceBase } from '../servicebase';
import { Filter } from 'services/models/odata/filter';
import { QueryParams } from 'services/models/odata/queryParams';
import { Response } from 'services/models/response';
import { OptionModel } from 'services/models/model';

@Injectable()
export class JobTitleGroupService extends ServiceBase<JobTitleGroupModel> {
  constructor(http: HttpService) {
    super('jobtitlegroup', http, environment.apiUrl);
  }

  createEntity(input: any): JobTitleGroupModel {
    return JobTitleGroupModel.create(input);
  }

  listByCompanyRootId(companyRootId: string): Observable<Response<JobTitleGroupModel[]>> {
    const filters = [];
    filters.push(Filter.buildFilter('companyRootId', companyRootId));
    filters.push(Filter.buildFilter('enable', true));

    const params = {
      select: [
        'id',
        'companyRootName',
        'companyRootId',
        'jobTitleFamilyId',
        'code',
        'name',
        'description',
        'enable',
      ],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: Filter.multEquals(filters),
    } as QueryParams;

    return this.list(params, true);
  }

  listByCompanies(companyIds: string[]): Observable<JobTitleGroupModel[]> {
    if (!companyIds || companyIds.length === 0) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    const companyFilter = companyIds.map(id => `companyRootId eq ${id}`).join(' or ');
    const finalFilter = `(${companyFilter}) and enable eq true`;

    const queryParams = [
      `$filter=${finalFilter}`,
      `$select=id,name,code,companyRootName`,
      `$orderby=name asc`,
    ];

    return this.http
      .get(`${this.endpoint}/odata?${queryParams.join('&')}`, null, null, null, true)
      .pipe(
        map((data: any) => {
          const items = data?.body ?? [];
          return Array.isArray(items)
            ? items.map(item => JobTitleGroupModel.create(item))
            : [];
        })
      );
  }

  getJobTitleGroupsByCompany(companyId: string): Observable<JobTitleGroupModel[]> {
    const params = {
      select: ['id', 'name', 'description', 'code', 'enable', 'hasSeniority'],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: Filter.equal('enable', true),
    } as QueryParams;

    const query = this.createBuildQuery(params);

    return this.http.get(`${this.endpoint}/list-by-company/${companyId}?${query}`, null, null, null, true).pipe(
      map(res => {
        return res.body.map(item => JobTitleGroupModel.create(item));
      })
    );
  }

  loadGroupsByName(companyRootId: string, char: string): Observable<JobTitleGroupModel[]> {
    if (!companyRootId) return of([]);

    const search = (char || '').toString();

    const filters = [];
    filters.push(Filter.buildFilter('companyRootId', companyRootId));
    if (search && search.trim().length > 0) {
      const containsFilter = f()
        .contains(x => x.toLower('name'), search.toLocaleLowerCase())
        .toString();
      filters.push(containsFilter);
    }
    filters.push(Filter.buildFilter('enable', true));

    const params = {
      select: ['id', 'name', 'code', 'companyRootId'],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: Filter.multEquals(filters),
    } as QueryParams;

    return this.list(params).pipe(map(res => res.data.filter(x => x.enable)));
  }

  loadGroupsByCompanyGroup(companyGroupId: string, char: string): Observable<JobTitleGroupModel[]> {
    if (!companyGroupId) return of([]);

    const search = (char || '').trim().toLowerCase();
    const filter =
      search.length > 0
        ? f()
            .and(f().eq('companyGroupId', companyGroupId).toString().replace(/'/g, ''))
            .and(
              f()
                .contains((x: any) => x.toLower('name'), search)
                .or(f().contains((x: any) => x.toLower('code'), search))
            )
            .toString()
        : f().eq('companyGroupId', companyGroupId).toString().replace(/'/g, '');

    const params = {
      select: ['id', 'name', 'code', 'companyRootName', 'companyGroupId'],
      orderBy: 'name',
      orderByOrder: 'asc',
      filter: buildQuery({ filter }).replace('?', '&'),
    } as QueryParams;

    return this.list(params, true).pipe(map(res => res.data.filter(x => x.enable)));
  }

  getListFilteredByCompanyRootId(companyRootId: string): Observable<JobTitleGroupModel[]> {
    if (!companyRootId) {
      return of([]);
    }

    const queryParams = [
      `$select=id,name`,
      `$filter=companyRootId eq ${companyRootId} and enable eq true`,
      `$orderby=name asc`,
    ];

    return this.http
      .get(`${this.endpoint}/odata?${queryParams.join('&')}`, null, null, null, true)
      .pipe(map((data: any) => data.body.map(item => JobTitleGroupModel.create(item))));
  }

  listByJobTitleFamilies(
    companyRootId: string,
    jobTitleFamilyIds: string[]
  ): Observable<Response<JobTitleGroupModel[]>> {
    if (!companyRootId || !jobTitleFamilyIds?.length) {
      const emptyResponse = new Response<JobTitleGroupModel[]>();
      emptyResponse.data = [];
      emptyResponse.count = 0;
      return of(emptyResponse);
    }

    const jobTitleGroupFilter = jobTitleFamilyIds
      .map(id => `jobTitleFamilyId eq ${id}`)
      .join(' or ');
    const finalFilter = `(${jobTitleGroupFilter}) and enable eq true and companyRootId eq ${companyRootId}`;

    const encodedCompanyRootId = encodeURIComponent(companyRootId);
    const encodedFamilyIds = encodeURIComponent(jobTitleFamilyIds.join(','));
    const encodedFirstFamilyId = encodeURIComponent(jobTitleFamilyIds[0]);

    const queryParams = [
      `$filter=${encodeURIComponent(finalFilter)}`,
      `$select=id, companyRootName, companyRootId, jobTitleFamilyId, code, name, description, enable`,
      `$orderby=name asc`,
      `companyRootId=${encodedCompanyRootId}`,
      `jobTitleFamilyIds=${encodedFamilyIds}`,
      `jobTitleFamilyId=${encodedFirstFamilyId}`,
    ];

    return this.http
      .get(`${this.endpoint}/odata?${queryParams.join('&')}`, null, null, null, true)
      .pipe(
        map(res => {
          const response = new Response<JobTitleGroupModel[]>();
          response.data = res.body.map(pp => this.createEntity(pp) as JobTitleGroupModel);
          response.count = res.headers.get('x-count');
          return response;
        })
      );
  }
}
